import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RAG_META } from "@/lib/rag";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtHM, fmtDate, fmtDT, fmtDateShort, exportFilename } from "@/lib/timefmt";
import DateField from "@/components/DateField";
import { ArrowUp, ArrowDown, X, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";
import MemberTimelineDialog from "@/components/MemberTimelineDialog";

export default function MemberOverview() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [tree, setTree] = useState({ units: [], transfer_team: null });
  const [terms, setTerms] = useState({ unit_label: "Unit", subunit_label: "Subunit", team_label: "Team" });
  const [members, setMembers] = useState([]);
  const [detailFor, setDetailFor] = useState(null);
  const [detailEntries, setDetailEntries] = useState([]);
  const [timelineFor, setTimelineFor] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const [filters, setFilters] = useState({ name: "", unit_id: "", subunit_id: "", team_id: "", date: "" });
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [showDays, setShowDays] = useState(2);
  const [view, setView] = useState("fatigue");
  const [scopeSel, setScopeSel] = useState("");
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => {
      setTree({ units: r.data.units, transfer_team: r.data.transfer_team });
      setTerms(r.data.terminology);
    }).catch(() => {});
    api.get(`/org/members?company_id=${companyId}`).then((r) => setMembers(r.data.members))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== "tasks" || !companyId) return;
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (showDays - 1));
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get(`/org/tasks?company_id=${companyId}&from_date=${iso(from)}&to_date=${iso(to)}`)
      .then((r) => setTasks(r.data)).catch(() => {});
  }, [view, companyId, showDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const names = useMemo(() => {
    const m = {};
    (tree.units || []).forEach((u) => {
      m[u.id] = u.name;
      (u.subunits || []).forEach((s) => {
        m[s.id] = s.name;
        (s.teams || []).forEach((t) => { m[t.id] = t.name; });
      });
    });
    if (tree.transfer_team) m[tree.transfer_team.id] = tree.transfer_team.name;
    return m;
  }, [tree]);

  const openDetail = async (m) => {
    setDetailFor(m);
    setDetailEntries([]);
    setOpenEntry(null);
    try {
      const { data } = await api.get(`/org/members/${m.id}/entries`);
      setDetailEntries(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setDetailFor(null);
    }
  };

  const orgOf = (m) => m.org || {};

  const allSubunits = (tree.units || []).flatMap((u) => u.subunits || []);
  const allTeams = allSubunits.flatMap((s) => s.teams || []);

  const scopeOptions = [];
  for (const u of tree.units || []) {
    scopeOptions.push({ value: `unit:${u.id}`, label: `${terms.unit_label}: ${u.name}` });
    for (const s of u.subunits || []) {
      scopeOptions.push({ value: `subunit:${s.id}`, label: `${u.name} / ${s.name}` });
      for (const t of s.teams || []) {
        scopeOptions.push({ value: `team:${t.id}`, label: `${u.name} / ${s.name} / ${t.name}` });
      }
    }
  }
  if (tree.transfer_team) scopeOptions.push({ value: `team:${tree.transfer_team.id}`, label: tree.transfer_team.name });

  const tasksByDay = useMemo(() => {
    const m = {};
    for (const t of tasks) {
      const uids = [t.created_by, ...(t.participants || []).map((p) => p.user_id)];
      for (const uid of uids) {
        const key = `${uid}|${t.date}`;
        (m[key] = m[key] || []).push(t);
      }
    }
    return m;
  }, [tasks]);

  const visible = useMemo(() => {
    let list = members.filter((m) => {
      const org = m.org || {};
      if (scopeSel) {
        const [kind, id] = scopeSel.split(":");
        if (kind === "unit" && org.unit_id !== id) return false;
        if (kind === "subunit" && org.subunit_id !== id) return false;
        if (kind === "team" && org.team_id !== id) return false;
      }
      if (filters.name && !`${m.first_name} ${m.last_name}`.toLowerCase().includes(filters.name.toLowerCase())) return false;
      if (filters.unit_id && org.unit_id !== filters.unit_id) return false;
      if (filters.subunit_id && org.subunit_id !== filters.subunit_id) return false;
      if (filters.team_id && org.team_id !== filters.team_id) return false;
      if (filters.date) {
        if (view === "tasks") { if (!(tasksByDay[`${m.id}|${filters.date}`] || []).length) return false; }
        else if (m.latest?.date !== filters.date) return false;
      }
      return true;
    });
    const val = (m) => {
      const org = m.org || {};
      switch (sort.key) {
        case "name": return `${m.last_name} ${m.first_name}`.toLowerCase();
        case "unit": return names[org.unit_id] || "";
        case "subunit": return names[org.subunit_id] || "";
        case "team": return names[org.team_id] || "";
        case "date": return m.latest?.date || "";
        case "yesterday": return m.yesterday ? m.yesterday.score_total : null;
        case "today": return m.latest ? m.latest.score_total : null;
        default: return "";
      }
    };
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [members, filters, sort, names, view, tasksByDay, scopeSel]);

  const exportBoardCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const durOf = (t) => {
      const [sh, sm] = (t.start || "0:0").split(":").map(Number);
      const [eh, em] = (t.end || "0:0").split(":").map(Number);
      let d = (eh * 60 + em) - (sh * 60 + sm);
      if (d <= 0) d += 1440;
      return d + (t.brief_min || 0) + (t.debrief_min || 0);
    };
    const header = ["Date", "Member", "External ID", terms.unit_label, terms.subunit_label, terms.team_label,
      "Task Type", "Name", "Kind", "Status", "Start", "End", "Total (min)", "Equipment", "Primary Location", "Additional Locations", "Crew", "Remarks"];
    const lines = [header.join(",")];
    let count = 0;
    for (const m of visible) {
      const org = m.org || {};
      for (const t of tasksByDay[m.id] || []) {
        lines.push([
          filters.date, `${m.first_name} ${m.last_name}`, m.external_user_id || "",
          names[org.unit_id] || "", names[org.subunit_id] || "", names[org.team_id] || "",
          t.task_type_name || "", t.name || "", t.kind || "task", t.status || "scheduled",
          t.start || "", t.end || "", durOf(t),
          (t.equipment_labels || []).join("; "),
          t.primary_location_name || "", (t.additional_location_names || []).join("; "),
          (t.participants || []).map((p) => p.name).join("; "),
          t.remarks || "",
        ].map(esc).join(","));
        count += 1;
      }
    }
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename("crew-board-tasks", "csv", user);
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${count} task(s) across ${visible.length} member(s)`);
  };

  const exportBoardFatigueCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const dayText = (dd) => {
      if (!dd) return "—";
      if (dd.entry) return `${(RAG_META[dd.entry.rag] || {}).label || ""} ${Number(dd.entry.score_total).toFixed(1)}`.trim();
      if (dd.on_duty && !dd.off_work) return "No entry";
      return "Off Duty";
    };
    const daySpecs = showDays === 2
      ? [{ label: "Yesterday", idx: 1 }, { label: "Today", idx: 0 }]
      : dayCols.map((d) => ({ label: fmtDateShort(d.date), date: d.date }));
    const header = ["Member", "External ID", terms.unit_label, terms.subunit_label, terms.team_label,
      "Last Entry", ...daySpecs.map((x) => x.label)];
    const lines = [header.join(",")];
    for (const m of visible) {
      const org = m.org || {};
      const days = m.days || [];
      const cells = daySpecs.map((spec) => dayText(spec.idx != null ? days[spec.idx] : days.find((x) => x.date === spec.date)));
      lines.push([`${m.first_name} ${m.last_name}`.trim() || m.email, m.external_user_id || "",
        names[org.unit_id] || "", names[org.subunit_id] || "", names[org.team_id] || "",
        fmtDate(m.latest?.date), ...cells].map(esc).join(","));
    }
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename("crew-board-fatigue", "csv", user);
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} member(s)`);
  };

  const hasFilters = filters.name || filters.unit_id || filters.subunit_id || filters.team_id || filters.date;
  const dayCols = (members[0]?.days || []).slice(0, showDays);

  return (
    <div>
      <div className="mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num" data-testid="dashboard-title">
          Fatigue Board · {new Date().toLocaleDateString()}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Crew Readiness Board</h1>
        <p className="text-slate-400 mt-1 text-sm">Latest score indicator for every member in your scope. Click a row for full details.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="board-controls">
          <div className="flex rounded-md border border-slate-800 overflow-hidden" data-testid="board-view-toggle">
            <button onClick={() => setView("fatigue")} data-testid="board-view-fatigue"
              className={`px-3 py-2.5 text-sm transition-colors ${view === "fatigue" ? "bg-blue-600/20 text-blue-300" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
              Fatigue
            </button>
            <button onClick={() => setView("tasks")} data-testid="board-view-tasks"
              className={`px-3 py-2.5 text-sm transition-colors ${view === "tasks" ? "bg-blue-600/20 text-blue-300" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
              {terms?.task_label_plural || "Tasks"}
            </button>
          </div>
          {view === "tasks" && (
            <select value={scopeSel} onChange={(e) => setScopeSel(e.target.value)} data-testid="board-scope-select"
              title="Show all members or narrow by org structure"
              className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
              <option value="">All members</option>
              {scopeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <button onClick={view === "tasks" ? exportBoardCsv : exportBoardFatigueCsv} data-testid={`board-${view}-export`}
            title="Download the current filtered/sorted view as CSV"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-sm text-slate-300 transition-colors">
            <Download className="h-3.5 w-3.5" /> Export view
          </button>
          {isSuper && companies.length > 1 && (
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="overview-company-select"
              className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={showDays} onChange={(e) => setShowDays(Number(e.target.value))} data-testid="overview-show-days"
            className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
            <option value={2}>Show: Yesterday + Today</option>
            <option value={7}>Show: 7 days</option>
            <option value={14}>Show: 14 days</option>
          </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-4 rounded-lg border border-slate-800 bg-slate-900/60 mb-4" data-testid="overview-filters">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Name</label>
          <input value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} data-testid="overview-filter-name"
            placeholder="Search…" className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{terms.unit_label}</label>
          <select value={filters.unit_id} onChange={(e) => setFilters({ ...filters, unit_id: e.target.value, subunit_id: "", team_id: "" })}
            data-testid="overview-filter-unit" className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {(tree.units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{terms.subunit_label}</label>
          <select value={filters.subunit_id} onChange={(e) => setFilters({ ...filters, subunit_id: e.target.value, team_id: "" })}
            data-testid="overview-filter-subunit" className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {allSubunits.filter((s) => !filters.unit_id || s.unit_id === filters.unit_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{terms.team_label}</label>
          <select value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}
            data-testid="overview-filter-team" className="w-full px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All</option>
            {allTeams.filter((t) => !filters.subunit_id || t.subunit_id === filters.subunit_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            {tree.transfer_team && <option value={tree.transfer_team.id}>{tree.transfer_team.name}</option>}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Date</label>
          <DateField value={filters.date} onChange={(v) => setFilters({ ...filters, date: v })} testId="overview-filter-date" />
        </div>
        <div className="flex items-end">
          <button onClick={() => setFilters({ name: "", unit_id: "", subunit_id: "", team_id: "", date: "" })} data-testid="overview-clear-filters"
            disabled={!hasFilters}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs disabled:opacity-40">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="member-overview">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="Name" k="name" sort={sort} setSort={setSort} className="sticky left-0 z-20 bg-slate-900 border-r border-slate-800/60 min-w-[170px]" />
                <SortTh label={terms.unit_label} k="unit" sort={sort} setSort={setSort} />
                <SortTh label={terms.subunit_label} k="subunit" sort={sort} setSort={setSort} />
                <SortTh label={terms.team_label} k="team" sort={sort} setSort={setSort} />
                <SortTh label="Last Entry" k="date" sort={sort} setSort={setSort} />
                {view === "tasks" ? (
                  showDays === 2 ? (
                    <>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Yesterday</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Today</th>
                    </>
                  ) : (
                    dayCols.map((d) => (
                      <th key={d.date} className="px-2 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num whitespace-nowrap">{fmtDateShort(d.date)}</th>
                    ))
                  )
                ) : showDays === 2 ? (
                  <>
                    <SortTh label="Yesterday" k="yesterday" sort={sort} setSort={setSort} />
                    <SortTh label="Today" k="today" sort={sort} setSort={setSort} />
                  </>
                ) : (
                  dayCols.map((d) => (
                    <th key={d.date} className="px-2 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num whitespace-nowrap">{fmtDateShort(d.date)}</th>
                  ))
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {visible.length === 0 && (
                <tr><td colSpan={5 + (showDays === 2 ? 2 : dayCols.length)} className="px-4 py-8 text-center text-slate-500">No members match.</td></tr>
              )}
              {visible.map((m) => {
                const org = orgOf(m);
                const latest = m.latest;
                const y = m.yesterday;
                const days = m.days || [];
                return (
                  <tr key={m.id} onClick={() => view === "tasks" ? setTimelineFor(m) : openDetail(m)} data-testid={`overview-row-${m.id}`}
                    className="group hover:bg-slate-800/50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-800/60 min-w-[170px] transition-colors">
                      <div className="font-medium">{`${m.first_name} ${m.last_name}`.trim() || m.email}</div>
                      <div className="text-xs text-slate-500 font-mono-num">{m.external_user_id}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{names[org.unit_id] || "—"}</td>
                    <td className="px-4 py-3 text-slate-300">{names[org.subunit_id] || "—"}</td>
                    <td className="px-4 py-3 text-slate-300">{names[org.team_id] || "—"}</td>
                    <td className="px-4 py-3 font-mono-num text-slate-300">{fmtDate(latest?.date)}</td>
                    {view === "tasks" ? (
                      showDays === 2 ? (
                        <>
                          <td className="px-4 py-3"><TaskCell items={tasksByDay[`${m.id}|${days[1]?.date}`]} testId={`board-tasks-yesterday-${m.id}`} /></td>
                          <td className="px-4 py-3"><TaskCell items={tasksByDay[`${m.id}|${days[0]?.date}`]} testId={`board-tasks-today-${m.id}`} /></td>
                        </>
                      ) : (
                        dayCols.map((d) => (
                          <td key={d.date} className="px-2 py-3">
                            <TaskCell items={tasksByDay[`${m.id}|${d.date}`]} testId={`board-tasks-${m.id}-${d.date}`} />
                          </td>
                        ))
                      )
                    ) : showDays === 2 ? (
                      <>
                        <td className="px-4 py-3"><DayCell day={days[1]} weight={m.score_weight} testId={`overview-yesterday-${m.id}`} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <DayCell day={days[0]} weight={m.score_weight} testId={`overview-indicator-${m.id}`} />
                            <ScoreDelta latest={latest} yesterday={y} testId={`score-delta-${m.id}`} />
                          </div>
                        </td>
                      </>
                    ) : (
                      dayCols.map((d) => {
                        const dd = days.find((x) => x.date === d.date);
                        return (
                          <td key={d.date} className="px-2 py-3">
                            <DayCell day={dd} weight={m.score_weight} testId={`overview-day-${m.id}-${d.date}`} />
                          </td>
                        );
                      })
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <MemberTimelineDialog member={timelineFor} onClose={() => setTimelineFor(null)} />

      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="member-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {detailFor ? `${`${detailFor.first_name} ${detailFor.last_name}`.trim() || detailFor.email}` : ""}
              <span className="ml-2 text-xs text-slate-500 font-mono-num font-normal">{detailFor?.external_user_id}</span>
            </DialogTitle>
          </DialogHeader>
          {detailEntries.length === 0 && <div className="text-sm text-slate-500 py-4">No entries for this member.</div>}
          <div className="space-y-2">
            {detailEntries.map((e) => {
              const meta = RAG_META[e.rag] || RAG_META.green;
              const isOpen = openEntry === e.id;
              return (
                <div key={e.id} className="rounded-md border border-slate-800 bg-slate-950/40 overflow-hidden" data-testid={`detail-entry-${e.id}`}>
                  <button onClick={() => setOpenEntry(isOpen ? null : e.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-900/60 text-sm">
                    <span className="font-mono-num">{fmtDate(e.date)}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle ${meta.dot}`} />
                      {meta.label} · {Number(e.score_total).toFixed(1)}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-800/70 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(e.breakdown || []).map((b) => {
                          const m2 = RAG_META[b.rag] || RAG_META.green;
                          return (
                            <div key={b.question} className={`px-3 py-2 rounded-md text-xs ${m2.cls}`}>
                              <div className="opacity-70 font-mono-num uppercase tracking-wider text-[10px]">{b.question}</div>
                              <div>{b.label} <span className="opacity-70 font-mono-num">({b.value})</span></div>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Rest Periods</div>
                        {(e.rest_periods || []).length === 0 && <div className="text-xs text-slate-600">None logged.</div>}
                        {(e.rest_periods || []).map((r) => (
                          <div key={r.id} className="text-xs font-mono-num text-slate-300 flex flex-wrap gap-x-4 py-0.5">
                            <span>{fmtDT(r.start_time)} → {fmtDT(r.stop_time)}</span>
                            <span className="text-slate-400">{fmtHM(r.total_hours)} hrs:min</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskCell({ items, testId }) {
  if (!items || items.length === 0) return <span className="text-xs text-slate-600">—</span>;
  return (
    <div className="flex flex-col gap-1 items-start" data-testid={testId}>
      {items.map((t) => (
        <span key={t.id}
          title={`${t.task_type_name} ${t.start}–${t.end} · brief ${t.brief_min}m · debrief ${t.debrief_min}m · risk ${t.risk_total ?? 0} · complexity ${t.complexity_total ?? 0}${(t.participants || []).length ? ` · with ${t.participants.map((p) => p.name).join(", ")}` : ""}`}
          style={t.color && t.status !== "cancelled" ? { borderColor: t.color, color: t.color, background: `${t.color}1f` } : undefined}
          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono-num whitespace-nowrap border ${
            t.status === "cancelled" ? "bg-rose-400/15 text-rose-300 border-rose-400/40 line-through"
            : t.color ? "" : t.kind === "commitment"
              ? "bg-violet-600/15 text-violet-300 border-violet-600/40"
              : "bg-blue-600/15 text-blue-300 border-blue-600/40"}`}>
          {t.name || t.task_type_name} {t.start}–{t.end}
        </span>
      ))}
    </div>
  );
}

function DayCell({ day, testId, weight }) {
  if (!day) return <span className="text-xs text-slate-600">—</span>;
  if (day.entry) {
    const meta = RAG_META[day.entry.rag] || RAG_META.green;
    const w = Number(weight) || 1;
    return (
      <span data-testid={testId}
        className={`inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap ${meta.cls}`}>
        <span className={`inline-block h-2 w-2 rounded-full mr-2 ${meta.dot}`} />
        {meta.label} · {Number(day.entry.score_total).toFixed(1)}
        {w !== 1 && (
          <span className="ml-1.5 px-1 rounded bg-slate-950/50 border border-white/10 text-[9px] font-mono-num normal-case tracking-normal"
            title={`Score weighted ×${w} for this role`}
            data-testid={testId ? `${testId}-weight` : undefined}>×{w}</span>
        )}
      </span>
    );
  }
  if (day.on_duty && !day.off_work) {
    return (
      <span data-testid={testId}
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap border border-amber-500/70 text-amber-400">
        No entry
      </span>
    );
  }
  return (
    <span data-testid={testId}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap bg-slate-500/15 text-slate-400 border border-slate-600/40">
      Off Duty
    </span>
  );
}

function SortTh({ label, k, sort, setSort, className = "" }) {
  const active = sort.key === k;
  return (
    <th className={`px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num ${className}`}>
      <button onClick={() => setSort((s) => ({ key: k, dir: active && s.dir === "asc" ? "desc" : "asc" }))}
        data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-slate-300 transition-colors ${active ? "text-blue-400" : ""}`}>
        {label}
        {active && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

export function ScoreDelta({ latest, yesterday, testId }) {
  if (!latest || !yesterday) return null;
  const diff = Number(latest.score_total) - Number(yesterday.score_total);
  if (!Number.isFinite(diff) || Math.abs(diff) < 0.05) {
    return (
      <span data-testid={testId} title="No change vs yesterday"
        className="inline-flex items-center gap-0.5 text-[10px] font-mono-num text-slate-400">
        <Minus className="h-3 w-3" />0.0
      </span>
    );
  }
  const worse = diff > 0;
  const Icon = worse ? TrendingUp : TrendingDown;
  return (
    <span data-testid={testId} title={`${worse ? "Up" : "Down"} ${Math.abs(diff).toFixed(1)} vs yesterday`}
      className={`inline-flex items-center gap-0.5 text-[10px] font-mono-num ${worse ? "text-rose-400" : "text-emerald-400"}`}>
      <Icon className="h-3 w-3" />{Math.abs(diff).toFixed(1)}
    </span>
  );
}
